/**
 * LicenseBar Component
 */

import {
  getLicenseUsagesFromQueryString,
  usageToString,
} from '../../../../redux/helpers/license';
import './styles.scss';

import * as classNames from 'classnames';
import throttle from 'raf-throttle';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import * as WindowSizeListener from 'react-window-size-listener';

import { QueryStringContext } from '../../../../contexts/queryString';
import { hasSearchFilters } from '../../../../contexts/queryString/selectors';
import { IQueryStringState } from '../../../../contexts/queryString/types';
import { setLicenseBarHeight } from '../../../../redux/actions/ui';
import SessionLicenseBuilderButton from '../../../atoms/buttons/LicenseBuilderButton/SessionLicenseBuilderButton';
import { Span } from '../../../atoms/text/Label';
import { SearchSortOrderSelectWithContext } from '../../../molecules/forms/Select/SearchSortOrderSelect';
import FilterBar from '../../../organisms/search/FilterBar';
import { BrowsePagePath } from '../../../pages/browse/Browse';
import CurrencySelect from '../../forms/Select/CurrencySelect';

interface IOwnStateProps {
  licenseUsages: ILicenseUsage[];
  showLicenseBar: boolean;
  hasFilters: boolean;
  showSearchControls: boolean;
  fullWidth: boolean;
  heightPx: number;
}

interface IOwnDispatchProps {
  setLicenseBarHeight: (heightPx: number) => void;
}

interface IOwnProps {
  className?: string;
}

interface IQueryStringContextProps {
  params: IQueryStringState['license_params'];
  hasFilters: boolean;
}

type LicenseBarProps =
  & IOwnProps
  & IOwnStateProps
  & IQueryStringContextProps;

// tslint:disable-next-line:no-empty-interface
interface IOwnState {
}

const classes = new BEM('license-bar');
const defaultList = [];

class LicenseBar extends React.Component<LicenseBarProps & IOwnDispatchProps, IOwnState> {
  private wrapper = React.createRef<HTMLDivElement>();
  private fixedGuard = React.createRef<HTMLDivElement>();
  private throttledResize: () => void;

  constructor(props) {
    super(props);

    this.throttledResize = throttle(this.onResize, 100);

    this.state = {};
  }

  componentDidUpdate(prevProps: LicenseBarProps) {
    if (prevProps.licenseUsages.length !== this.props.licenseUsages.length) {
      this.onResize();
    }
    this.onLicenseBarResize();
  }

  onLicenseBarResize = () => {
    if (this.wrapper.current && this.fixedGuard.current) {
      const newHeight = this.wrapper.current.getBoundingClientRect().height;
      if (newHeight !== this.props.heightPx) {
        this.fixedGuard.current.style.height = `${newHeight}px`;
        this.props.setLicenseBarHeight(newHeight);
      }
    }
  }

  onResize = () => {
    this.onLicenseBarResize();
  }

  renderNoLicenseMessage(modifiers) {
    const { hasFilters } = this.props;
    const noTermsMods = {
      'no-terms': true,
      'no-filters': !hasFilters,
    };
    return (
      <div {...classes('', modifiers) }>
        <div {...classes('scroll-wrapper') }>
          {this.renderSearchControls()}
          <div {...classes('scroller', 'no-terms', noTermsMods)}>
            <SessionLicenseBuilderButton
              label={<Span text="Show Prices" variant="description" size="large" />}
              colorScheme="accent"
              size="small"
              variant="pill"
              withShadow
              {...classes('button-set-terms') }
            />
          </div>
        </div>
        {this.renderSortControls()}
      </div>
    );
  }

  renderLicenseUsages() {
    const { licenseUsages } = this.props;
    return (
      <>
        <div {...classes('')}>
          <div {...classes('scroll-wrapper')}>
            {this.renderSearchControls()}
            <div {...classes('scroller')}>
              <div {...classes('usages') }>
                {licenseUsages.map((usage, index) => (
                  <SessionLicenseBuilderButton
                    key={`license-usage-${index}-${usage.medium.id}`}
                    {...classes('usage-button') }
                    label={<Span text={usageToString(usage)} variant="description" size="large" />}
                    colorScheme="accent"
                    variant="pill"
                    size="small"
                    selectedUsage={index}
                  />
                ))}
                <CurrencySelect
                  {...classes('currency-select') }
                />
              </div>
            </div>
          </div>
          {this.renderSortControls()}
        </div>
      </>
    );
  }

  renderSearchControls() {
    const { showSearchControls, hasFilters } = this.props;
    return (
      <>
        {hasFilters && showSearchControls && <FilterBar {...classes('filter-bar') } />}
      </>
    );
  }

  renderSortControls() {
    const { showSearchControls, hasFilters } = this.props;
    return (
      <>
        {showSearchControls && hasFilters && (
          <div {...classes('sort-wrapper')}>
            <Span variant="label" size="medium" {...classes('sort-label')}>Sort&nbsp;by:</Span>
            <SearchSortOrderSelectWithContext />
          </div>
        )}
      </>
    );
  }

  render() {
    const {
      className,
      fullWidth,
      licenseUsages,
      showLicenseBar,
    } = this.props;

    const hasUsages = licenseUsages.length > 0;

    const wrapperExtraClasses = classNames(
      className,
    );

    const modifiers = {
      'with-terms': hasUsages,
      'full-width': fullWidth,
    };

    return showLicenseBar ? (
      <>
        <div ref={this.fixedGuard} {...classes('fixed-guard')} />
        <div ref={this.wrapper} {...classes('wrapper', modifiers, wrapperExtraClasses)}>
          {hasUsages ? this.renderLicenseUsages() : this.renderNoLicenseMessage(modifiers)}
        </div>
        <WindowSizeListener onResize={this.throttledResize} />
      </>
    ) : <WindowSizeListener onResize={this.throttledResize} />;
  }
}

// tslint:disable-next-line:max-line-length
const ConnectedLicenseBar = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & IQueryStringContextProps, IStoreState>(
  ({ staticData: { licenseComponents }, found, ui }, { params, hasFilters }) => ({
    showLicenseBar: ui.header.type === 'simple',
    showSearchControls: found.match.location.pathname === BrowsePagePath && hasFilters,
    fullWidth: found.match.location.pathname === BrowsePagePath,
    hasFilters,
    heightPx: ui.licenseBar.heightPx,
    licenseUsages: params ? getLicenseUsagesFromQueryString({
      components: licenseComponents as GQL.ILicenseComponents,
      params,
    }) : defaultList,
  }),
  (dispatch) => ({
    setLicenseBarHeight: (heightPx: number) => dispatch(setLicenseBarHeight(heightPx)),
  }),
)(LicenseBar);

const LicenseBarWithQueryStringContext: React.SFC<IOwnProps> = (props) => (
  <QueryStringContext.Consumer>
    {(ctx) => (
      <ConnectedLicenseBar
        params={ctx.license_params}
        hasFilters={hasSearchFilters(ctx)}
        {...props}
      />
    )}
  </QueryStringContext.Consumer>
);

export default LicenseBarWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/molecules/blocks/LicenseBar/index.tsx